import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import { 
  Users, Crown, Sparkles, Building2, FileText, Target, 
  Download, Search, RefreshCw, KeyRound, Edit, Eye,
  Shield, TrendingUp, Calendar, Mail, MessageSquarePlus,
  Lightbulb, Bug, FileQuestion, CheckCircle, Clock, AlertCircle,
  BarChart3
} from 'lucide-react';
import axios from 'axios';
import { AnalyticsPanel } from '../components/AnalyticsPanel';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AdminDashboard() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [totalUsers, setTotalUsers] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [planFilter, setPlanFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(0);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetailOpen, setUserDetailOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [resetPasswordOpen, setResetPasswordOpen] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('users');
  const [feedback, setFeedback] = useState([]);
  const [feedbackStats, setFeedbackStats] = useState({});

  const USERS_PER_PAGE = 20;

  // Check admin access
  useEffect(() => {
    const checkAdmin = async () => {
      if (!token) {
        navigate('/login');
        return;
      }
      
      try {
        const res = await axios.get(`${API}/api/admin/check`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.data.is_admin) {
          setIsAdmin(true);
          fetchStats();
          fetchUsers();
          fetchFeedback();
        } else {
          toast.error('Admin access required');
          navigate('/dashboard');
        }
      } catch (err) {
        toast.error('Admin access required');
        navigate('/dashboard');
      } finally {
        setLoading(false);
      }
    };
    
    checkAdmin();
  }, [token, navigate]);

  const fetchStats = async () => {
    try {
      const res = await axios.get(`${API}/api/admin/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(res.data);
    } catch (err) {
      toast.error('Failed to load stats');
    }
  };

  const fetchUsers = async (search = searchQuery, plan = planFilter, page = currentPage) => {
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (plan && plan !== 'all') params.append('plan', plan);
      params.append('skip', page * USERS_PER_PAGE);
      params.append('limit', USERS_PER_PAGE);
      
      const res = await axios.get(`${API}/api/admin/users?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(res.data.users);
      setTotalUsers(res.data.total);
    } catch (err) {
      toast.error('Failed to load users');
    }
  };

  const fetchFeedback = async () => {
    try {
      const res = await axios.get(`${API}/api/admin/feedback`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFeedback(res.data.feedback || []);
      setFeedbackStats(res.data.stats || {});
    } catch (err) {
      console.error('Failed to load feedback:', err);
    }
  };

  const updateFeedbackStatus = async (feedbackId, newStatus) => {
    try {
      await axios.put(`${API}/api/admin/feedback/${feedbackId}?status=${newStatus}`, null, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Feedback status updated');
      fetchFeedback();
    } catch (err) {
      toast.error('Failed to update feedback');
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(0);
    fetchUsers(searchQuery, planFilter, 0);
  };

  const handlePlanFilterChange = (value) => {
    setPlanFilter(value);
    setCurrentPage(0);
    fetchUsers(searchQuery, value, 0);
  };

  const openUserDetail = async (userId) => {
    try {
      const res = await axios.get(`${API}/api/admin/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedUser(res.data);
      setUserDetailOpen(true);
    } catch (err) {
      toast.error('Failed to load user details');
    }
  };

  const openEditDialog = (user) => {
    setEditingUser({
      ...user,
      subscription_plan: user.subscription_plan || 'free',
      is_lifetime_pro: user.is_lifetime_pro || false,
      lifetime_pro_reason: user.lifetime_pro_reason || '',
      notes: user.admin_notes || ''
    });
    setEditDialogOpen(true);
  };

  const saveUserChanges = async () => {
    if (!editingUser) return;
    setSaving(true);
    
    try {
      await axios.put(`${API}/api/admin/users/${editingUser.id}`, {
        subscription_plan: editingUser.subscription_plan,
        is_lifetime_pro: editingUser.is_lifetime_pro,
        lifetime_pro_reason: editingUser.lifetime_pro_reason,
        notes: editingUser.notes
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('User updated successfully');
      setEditDialogOpen(false);
      fetchUsers();
      fetchStats();
    } catch (err) {
      toast.error('Failed to update user');
    } finally {
      setSaving(false);
    }
  };

  const resetPassword = async () => {
    if (!editingUser || !newPassword) return;
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setSaving(true);
    try {
      await axios.post(`${API}/api/admin/users/${editingUser.id}/reset-password`, {
        new_password: newPassword
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Password reset for ${editingUser.email}`);
      setResetPasswordOpen(false);
      setNewPassword('');
    } catch (err) {
      toast.error('Failed to reset password');
    } finally {
      setSaving(false);
    }
  };

  const exportCSV = async () => {
    try {
      const res = await axios.get(`${API}/api/admin/users/export/csv`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'pitchpal_users.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Users exported to CSV');
    } catch (err) {
      toast.error('Failed to export users');
    }
  };

  const getPlanBadge = (plan, isLifetime) => {
    if (isLifetime) {
      return <Badge className="bg-gradient-to-r from-amber-500 to-orange-500 text-white">Lifetime Pro</Badge>;
    }
    switch (plan) {
      case 'pro':
        return <Badge className="bg-primary text-white">Pro</Badge>;
      case 'trial':
        return <Badge className="bg-blue-500 text-white">Trial</Badge>;
      case 'basic':
        return <Badge className="bg-stone-500 text-white">Basic</Badge>;
      default:
        return <Badge variant="outline">Free</Badge>;
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!isAdmin) {
    return null;
  }

  return (
    <div className="min-h-screen bg-stone-50 pt-20 pb-12" data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <Shield className="w-8 h-8 text-primary" />
            <h1 className="text-3xl font-heading font-bold text-stone-900">Admin Dashboard</h1>
          </div>
          <p className="text-stone-600">Manage users, view analytics, and control account settings</p>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-8">
            <Card className="bg-white">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-primary-50 rounded-lg">
                    <Users className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-stone-900">{stats.users.total}</p>
                    <p className="text-xs text-stone-500">Total Users</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-white">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-50 rounded-lg">
                    <TrendingUp className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-stone-900">{stats.users.recent_signups}</p>
                    <p className="text-xs text-stone-500">Last 7 Days</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-white">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-amber-50 rounded-lg">
                    <Crown className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-stone-900">{stats.users.by_plan.pro}</p>
                    <p className="text-xs text-stone-500">Pro Users</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-white">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-50 rounded-lg">
                    <Sparkles className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-stone-900">{stats.users.by_plan.trial}</p>
                    <p className="text-xs text-stone-500">On Trial</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-white">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-50 rounded-lg">
                    <Building2 className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-stone-900">{stats.content.organizations}</p>
                    <p className="text-xs text-stone-500">Organizations</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-white">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-rose-50 rounded-lg">
                    <FileText className="w-5 h-5 text-rose-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-stone-900">{stats.content.saved_templates}</p>
                    <p className="text-xs text-stone-500">Saved Content</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Tab Navigation */}
        <div className="flex gap-2 mb-6 flex-wrap" data-testid="admin-tabs">
          <Button
            variant={activeTab === 'users' ? 'default' : 'outline'}
            onClick={() => setActiveTab('users')}
            className={activeTab === 'users' ? 'bg-primary text-white' : ''}
            data-testid="admin-tab-users"
          >
            <Users className="w-4 h-4 mr-2" />
            Users ({totalUsers})
          </Button>
          <Button
            variant={activeTab === 'analytics' ? 'default' : 'outline'}
            onClick={() => setActiveTab('analytics')}
            className={activeTab === 'analytics' ? 'bg-primary text-white' : ''}
            data-testid="admin-tab-analytics"
          >
            <BarChart3 className="w-4 h-4 mr-2" />
            Site Analytics
          </Button>
          <Button
            variant={activeTab === 'feedback' ? 'default' : 'outline'}
            onClick={() => setActiveTab('feedback')}
            className={activeTab === 'feedback' ? 'bg-primary text-white' : ''}
            data-testid="admin-tab-feedback"
          >
            <MessageSquarePlus className="w-4 h-4 mr-2" />
            Feedback
            {feedbackStats.new > 0 && (
              <span className="ml-2 bg-red-500 text-white text-xs font-bold px-1.5 py-0.5 rounded-full">{feedbackStats.new}</span>
            )}
          </Button>
        </div>

        {/* Analytics Tab */}
        {activeTab === 'analytics' && (
          <AnalyticsPanel token={token} />
        )}

        {/* Feedback Tab */}
        {activeTab === 'feedback' && (
          <Card className="bg-white" data-testid="feedback-admin-panel">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <MessageSquarePlus className="w-5 h-5" />
                    Feedback & Requests
                  </CardTitle>
                  <CardDescription>
                    {feedbackStats.total || 0} total — {feedbackStats.new || 0} new, {feedbackStats.in_progress || 0} in progress, {feedbackStats.completed || 0} completed
                  </CardDescription>
                </div>
                <Button variant="outline" size="sm" onClick={fetchFeedback}>
                  <RefreshCw className="w-4 h-4 mr-1" /> Refresh
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {feedback.length === 0 ? (
                <p className="text-center text-stone-500 py-8">No feedback yet</p>
              ) : (
                <div className="space-y-3">
                  {feedback.map((f) => {
                    const typeConfig = {
                      feedback: { icon: MessageSquarePlus, label: 'Feedback', color: 'bg-blue-100 text-blue-700' },
                      content_request: { icon: FileQuestion, label: 'Content Request', color: 'bg-amber-100 text-amber-700' },
                      tool_request: { icon: Lightbulb, label: 'Feature Request', color: 'bg-green-100 text-green-700' },
                      bug_report: { icon: Bug, label: 'Bug Report', color: 'bg-red-100 text-red-700' },
                    }[f.type] || { icon: MessageSquarePlus, label: f.type, color: 'bg-stone-100 text-stone-700' };
                    
                    const statusConfig = {
                      new: { icon: AlertCircle, label: 'New', color: 'bg-blue-100 text-blue-700' },
                      in_progress: { icon: Clock, label: 'In Progress', color: 'bg-amber-100 text-amber-700' },
                      completed: { icon: CheckCircle, label: 'Done', color: 'bg-green-100 text-green-700' },
                    }[f.status] || { icon: AlertCircle, label: f.status, color: 'bg-stone-100 text-stone-700' };
                    
                    const TypeIcon = typeConfig.icon;

                    return (
                      <div key={f.id} className={`p-4 rounded-lg border ${f.status === 'new' ? 'border-blue-200 bg-blue-50/30' : 'border-stone-200'}`} data-testid={`feedback-item-${f.id}`}>
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1">
                            <div className="flex flex-wrap items-center gap-2 mb-1">
                              <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${typeConfig.color}`}>
                                <TypeIcon className="w-3 h-3" /> {typeConfig.label}
                              </span>
                              <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${statusConfig.color}`}>
                                {statusConfig.label}
                              </span>
                              <span className="text-xs text-stone-400">
                                {new Date(f.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                              </span>
                            </div>
                            <h4 className="font-semibold text-sm text-stone-900">{f.subject}</h4>
                            <p className="text-sm text-stone-600 mt-1">{f.message}</p>
                            <p className="text-xs text-stone-400 mt-2">
                              From: <span className="font-medium text-stone-600">{f.user_email}</span>
                              {f.organization_name && <> ({f.organization_name})</>}
                            </p>
                          </div>
                          <div className="flex-shrink-0">
                            <Select value={f.status} onValueChange={(val) => updateFeedbackStatus(f.id, val)}>
                              <SelectTrigger className="w-[130px] h-8 text-xs">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="new">New</SelectItem>
                                <SelectItem value="in_progress">In Progress</SelectItem>
                                <SelectItem value="completed">Completed</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Users Table */}
        {activeTab === 'users' && (
        <Card className="bg-white">
          <CardHeader>
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Mail className="w-5 h-5" />
                  User Management
                </CardTitle>
                <CardDescription>View and manage all registered users</CardDescription>
              </div>
              
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => { fetchStats(); fetchUsers(); }}>
                  <RefreshCw className="w-4 h-4 mr-1" />
                  Refresh
                </Button>
                <Button variant="outline" size="sm" onClick={exportCSV}>
                  <Download className="w-4 h-4 mr-1" />
                  Export CSV
                </Button>
              </div>
            </div>
            
            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-3 mt-4">
              <form onSubmit={handleSearch} className="flex-1 flex gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-stone-400" />
                  <Input
                    placeholder="Search by email or organization..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9"
                    data-testid="admin-search-input"
                  />
                </div>
                <Button type="submit" variant="secondary">Search</Button>
              </form>
              
              <Select value={planFilter} onValueChange={handlePlanFilterChange}>
                <SelectTrigger className="w-[150px]" data-testid="admin-plan-filter">
                  <SelectValue placeholder="All Plans" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Plans</SelectItem>
                  <SelectItem value="free">Free</SelectItem>
                  <SelectItem value="basic">Basic</SelectItem>
                  <SelectItem value="pro">Pro</SelectItem>
                  <SelectItem value="trial">Trial</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
          
          <CardContent>
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Email</TableHead>
                    <TableHead>Organization</TableHead>
                    <TableHead>Plan</TableHead>
                    <TableHead>Joined</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-8 text-stone-500">
                        No users found
                      </TableCell>
                    </TableRow>
                  ) : (
                    users.map((u) => (
                      <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                        <TableCell className="font-medium">{u.email}</TableCell>
                        <TableCell>{u.organization_name || '-'}</TableCell>
                        <TableCell>{getPlanBadge(u.subscription_plan, u.is_lifetime_pro)}</TableCell>
                        <TableCell>{formatDate(u.created_at)}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => openUserDetail(u.id)}
                              data-testid={`view-user-${u.id}`}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => openEditDialog(u)}
                              data-testid={`edit-user-${u.id}`}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => { setEditingUser(u); setResetPasswordOpen(true); }}
                              data-testid={`reset-password-${u.id}`}
                            >
                              <KeyRound className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
            
            {/* Pagination */}
            <div className="flex items-center justify-between mt-4">
              <p className="text-sm text-stone-500">
                Showing {currentPage * USERS_PER_PAGE + 1} - {Math.min((currentPage + 1) * USERS_PER_PAGE, totalUsers)} of {totalUsers} users
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={currentPage === 0}
                  onClick={() => { setCurrentPage(p => p - 1); fetchUsers(searchQuery, planFilter, currentPage - 1); }}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={(currentPage + 1) * USERS_PER_PAGE >= totalUsers}
                  onClick={() => { setCurrentPage(p => p + 1); fetchUsers(searchQuery, planFilter, currentPage + 1); }}
                >
                  Next
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
        )}
      </div>

      {/* User Detail Dialog */}
      <Dialog open={userDetailOpen} onOpenChange={setUserDetailOpen}>
        <DialogContent className="max-w-lg bg-white">
          <DialogHeader>
            <DialogTitle>User Details</DialogTitle>
            <DialogDescription>Complete information about this user</DialogDescription>
          </DialogHeader>
          
          {selectedUser && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-stone-500 text-xs">Email</Label>
                  <p className="font-medium">{selectedUser.user.email}</p>
                </div>
                <div>
                  <Label className="text-stone-500 text-xs">Organization</Label>
                  <p className="font-medium">{selectedUser.user.organization_name || '-'}</p>
                </div>
                <div>
                  <Label className="text-stone-500 text-xs">Plan</Label>
                  <div className="mt-1">{getPlanBadge(selectedUser.user.subscription_plan, selectedUser.user.is_lifetime_pro)}</div>
                </div>
                <div>
                  <Label className="text-stone-500 text-xs">Joined</Label>
                  <p className="font-medium">{formatDate(selectedUser.user.created_at)}</p>
                </div>
              </div>
              
              {selectedUser.user.is_lifetime_pro && (
                <div className="p-3 bg-amber-50 rounded-lg">
                  <Label className="text-amber-700 text-xs">Lifetime Pro Reason</Label>
                  <p className="text-amber-900 font-medium">{selectedUser.user.lifetime_pro_reason || 'Not specified'}</p>
                </div>
              )}
              
              <div className="border-t pt-4">
                <Label className="text-stone-500 text-xs mb-2 block">User Activity</Label>
                <div className="grid grid-cols-4 gap-2 text-center">
                  <div className="p-2 bg-stone-50 rounded">
                    <p className="text-lg font-bold">{selectedUser.stats.partners}</p>
                    <p className="text-xs text-stone-500">Partners</p>
                  </div>
                  <div className="p-2 bg-stone-50 rounded">
                    <p className="text-lg font-bold">{selectedUser.stats.programs}</p>
                    <p className="text-xs text-stone-500">Programs</p>
                  </div>
                  <div className="p-2 bg-stone-50 rounded">
                    <p className="text-lg font-bold">{selectedUser.stats.grants}</p>
                    <p className="text-xs text-stone-500">Grants</p>
                  </div>
                  <div className="p-2 bg-stone-50 rounded">
                    <p className="text-lg font-bold">{selectedUser.stats.saved_templates}</p>
                    <p className="text-xs text-stone-500">Templates</p>
                  </div>
                </div>
              </div>
              
              {selectedUser.user.admin_notes && (
                <div className="border-t pt-4">
                  <Label className="text-stone-500 text-xs">Admin Notes</Label>
                  <p className="text-stone-700 mt-1">{selectedUser.user.admin_notes}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-md bg-white">
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
            <DialogDescription>{editingUser?.email}</DialogDescription>
          </DialogHeader>
          
          {editingUser && (
            <div className="space-y-4">
              <div>
                <Label>Subscription Plan</Label>
                <Select 
                  value={editingUser.subscription_plan} 
                  onValueChange={(v) => setEditingUser({...editingUser, subscription_plan: v})}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="free">Free</SelectItem>
                    <SelectItem value="basic">Basic</SelectItem>
                    <SelectItem value="pro">Pro</SelectItem>
                    <SelectItem value="trial">Trial</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="lifetime-pro"
                  checked={editingUser.is_lifetime_pro}
                  onChange={(e) => setEditingUser({...editingUser, is_lifetime_pro: e.target.checked})}
                  className="w-4 h-4"
                />
                <Label htmlFor="lifetime-pro" className="cursor-pointer">Lifetime Pro (Never Expires)</Label>
              </div>
              
              {editingUser.is_lifetime_pro && (
                <div>
                  <Label>Lifetime Pro Reason</Label>
                  <Input
                    value={editingUser.lifetime_pro_reason}
                    onChange={(e) => setEditingUser({...editingUser, lifetime_pro_reason: e.target.value})}
                    placeholder="e.g., Founder, Early Supporter, Partner"
                    className="mt-1"
                  />
                </div>
              )}
              
              <div>
                <Label>Admin Notes</Label>
                <textarea
                  value={editingUser.notes}
                  onChange={(e) => setEditingUser({...editingUser, notes: e.target.value})}
                  placeholder="Internal notes about this user..."
                  className="mt-1 w-full p-2 border rounded-md text-sm min-h-[80px]"
                />
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>Cancel</Button>
            <Button onClick={saveUserChanges} disabled={saving}>
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={resetPasswordOpen} onOpenChange={setResetPasswordOpen}>
        <DialogContent className="max-w-sm bg-white">
          <DialogHeader>
            <DialogTitle>Reset Password</DialogTitle>
            <DialogDescription>Set a new password for {editingUser?.email}</DialogDescription>
          </DialogHeader>
          
          <div>
            <Label>New Password</Label>
            <Input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Enter new password (min 6 chars)"
              className="mt-1"
              data-testid="new-password-input"
            />
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => { setResetPasswordOpen(false); setNewPassword(''); }}>
              Cancel
            </Button>
            <Button onClick={resetPassword} disabled={saving || newPassword.length < 6}>
              {saving ? 'Resetting...' : 'Reset Password'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
